import type { Api } from './api';
import { createHttpApi } from './http';
import { createMockApi } from './mock/mockApi';
import { createChatLive, type ChatLive } from './realtime/chatSocket';
import { dropExpiredSession, readToken } from './http/session';
import { migrateLegacyStorage } from './storage';

/**
 * The single data source of the app. Pages never import this directly: they use the hooks in
 * services/queries.
 *
 *   VITE_DATA_SOURCE=mock | http   explicit choice
 *   (unset)                        `http` when REACT_APP_BASE_URL is set, else `mock`
 *
 * In dev (`npm run dev`), Vite proxies /api, /uploads, /static and /socket.io to VITE_API_PROXY
 * (default http://127.0.0.1:5055, the local backend): the base URL is then empty (same origin, no
 * CORS). A build uses REACT_APP_BASE_URL as the API origin, or the same origin when built with
 * VITE_API_SAME_ORIGIN=true (e.g. to test a build with `vite preview`, which proxies too).
 */
export function apiBaseUrl(env: ImportMetaEnv = import.meta.env): string {
  if (env.DEV || env.VITE_API_SAME_ORIGIN === 'true') return '';
  return (env.REACT_APP_BASE_URL ?? '').replace(/\/$/, '');
}

/*
 * Written on the `import.meta.env.*` literals that Vite replaces at build time: the condition folds
 * to a constant, so an http build drops the demo data and its 6 MB of photos (see vite.config.ts),
 * and a mock build drops the http layer.
 */
const useHttp =
  import.meta.env.VITE_DATA_SOURCE === 'http' ||
  (import.meta.env.VITE_DATA_SOURCE !== 'mock' && !!import.meta.env.REACT_APP_BASE_URL);

export const api: Api = useHttp
  ? createHttpApi({
      baseUrl: apiBaseUrl(),
      activitiesEnabled: import.meta.env.REACT_APP_ACTIVITIES_ENABLED !== 'false',
      // Results page size (P15); only tests set it, to page through a small local fleet.
      resultsPageSize: Number(import.meta.env.VITE_RESULTS_PAGE_SIZE) || undefined,
    })
  : createMockApi();

/**
 * Live chat (P8): the Socket.IO connection to the same backend (http only; the demo data has none, so
 * the messages keep polling). Started and stopped by the shell (services/realtime/useChatLive).
 */
export const chatLive: ChatLive | null = useHttp ? createChatLive({ url: apiBaseUrl() }) : null;
/** The JWT the live connection authenticates with (null in the demo). */
export const chatToken = (): string | null => (useHttp ? readToken() : null);

/**
 * Boot, before the first render (main.tsx): the old site's keys (L3 and L6 removed, L5 currency carried over,
 * Decision 21) and a token already expired at load, dropped silently (AU-01: signed out on the same page).
 */
export function bootStorage(): void {
  migrateLegacyStorage();
  dropExpiredSession();
}

/** The session expired during the visit (CK-02, AU-02): the shell's listener (http only; the demo never expires). */
export { onSessionExpired } from './http/session';

export { ApiError } from './api';
export type { Api } from './api';
